import React, { useEffect, useState } from "react";

import { Alert, Box, Button, Stack } from "@mui/material";

import { ClipLoader } from "react-spinners";

import {
    getGoogleDmaMappings,
    postGoogleDmaMappings,
} from "helpers/destinations/googleDma";

import SwalAlertComp from "components/SwalAlert";

import {
    areGoogleAdsMappingsValid,
    getConversionsFetchErrorMessage,
} from "./GoogleAdsConversion.helpers";
import GoogleAdsConversionMappingContent from "./GoogleAdsConversionMappingContent";

const EMPTY_MAPPING = { conversion_id: "", event: "", conversion_name: "" };

const GoogleAdsConversionMappingEdit = ({
    destinationId,
    type,
    onSaveSuccess,
}) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [error, setError] = useState(null);

    const [conversions, setConversions] = useState([]);
    const [events, setEvents] = useState([]);
    const [selectedMappings, setSelectedMappings] = useState([]);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

    useEffect(() => {
        fetchMappings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [destinationId]);

    const fetchMappings = async () => {
        setLoading(true);
        setLoadError(null);
        setError(null);

        try {
            const response = await getGoogleDmaMappings(destinationId);

            if (response.status && response.data) {
                const data = response.data;

                setConversions(data.conversions?.all_conversions || []);
                setEvents(data.conversions?.all_events || []);

                if (data.mappings && data.mappings.length > 0) {
                    setSelectedMappings(
                        data.mappings.map((mapping) => {
                            return {
                                conversion_id:
                                    mapping.conversion_id?.toString(),
                                conversion_name: mapping.conversion_name || "",
                                event: mapping.event?.toString(),
                                isCustom: mapping.is_custom || false,
                            };
                        }),
                    );
                } else {
                    setSelectedMappings([{ ...EMPTY_MAPPING }]);
                }
            } else {
                setLoadError(
                    getConversionsFetchErrorMessage(type, response.message),
                );
            }
        } catch (err) {
            console.error("Error fetching Google Ads mappings:", err);
            setLoadError(
                getConversionsFetchErrorMessage(type, err?.message),
            );
        } finally {
            setLoading(false);
        }
    };

    const handleSaveMappings = async () => {
        setSaving(true);
        setError(null);

        try {
            const hasInvalidMapping = !areGoogleAdsMappingsValid(
                selectedMappings,
                conversions,
                type,
            );

            if (hasInvalidMapping) {
                setError(
                    type === "ggalc" || type === "ggass"
                        ? "Select an event and Google Ads conversion for every mapping. Each event can be mapped only once."
                        : "Please select both an event and Google Ads conversion for every mapping.",
                );
                setSaving(false);
                return;
            }

            const response = await postGoogleDmaMappings(destinationId, {
                mappings: selectedMappings.map((m) => ({
                    event: m.event?.toString(),
                    conversion_id: m.conversion_id?.toString(),
                    conversion_name:
                        m.conversion_name ||
                        conversions?.find(
                            (c) =>
                                c.id?.toString() ===
                                m.conversion_id?.toString(),
                        )?.name ||
                        "",
                })),
            });

            if (response.status) {
                SwalAlertComp({
                    title: "Success",
                    message: "Mappings saved successfully!",
                    icon: "success",
                    timer: 3000,
                });

                if (onSaveSuccess) {
                    setTimeout(() => {
                        onSaveSuccess();
                    }, 1000);
                }
            } else {
                setError(response.message || "Failed to save mappings");
            }
        } catch (err) {
            console.error("Error saving Google Ads mappings:", err);
            setError("An error occurred while saving mappings");
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        setIsSubmitDisabled(
            !areGoogleAdsMappingsValid(selectedMappings, conversions, type),
        );
    }, [selectedMappings, type, conversions]);

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
            >
                <ClipLoader size={24} color="#666" />
            </Box>
        );
    }

    return (
        <Stack spacing={2}>
            {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <GoogleAdsConversionMappingContent
                type={type}
                conversions={conversions}
                events={events}
                selectedMappings={selectedMappings}
                setSelectedMappings={setSelectedMappings}
                loadError={loadError}
                onRetry={fetchMappings}
            />

            {!loadError && conversions?.length > 0 && (
                <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveMappings}
                        disabled={saving || isSubmitDisabled}
                    >
                        {saving ? (
                            <>
                                <ClipLoader size={16} color="#fff" />
                                <span style={{ marginLeft: "8px" }}>
                                    Saving...
                                </span>
                            </>
                        ) : (
                            "Save Mappings"
                        )}
                    </Button>
                </Box>
            )}
        </Stack>
    );
};

export default GoogleAdsConversionMappingEdit;
